// Utilidades de geolocalización para el buscador por cercanía.
// Todo client-side: no requiere cambios en el backend.

import localidadCentros from "./localidad-centros.json";

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Distancia en kilómetros entre dos puntos (fórmula de Haversine).
 */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Convierte lat/lng (string o number) a coordenadas válidas, o null si no lo son.
 * Descarta el típico 0,0 que viene cuando el prestador no tiene geolocalización.
 */
export function parseCoords(
  lat: string | number | null | undefined,
  lng: string | number | null | undefined,
): LatLng | null {
  const latNum = typeof lat === 'string' ? parseFloat(lat) : lat;
  const lngNum = typeof lng === 'string' ? parseFloat(lng) : lng;

  if (
    typeof latNum !== 'number' || typeof lngNum !== 'number' ||
    Number.isNaN(latNum) || Number.isNaN(lngNum) ||
    latNum === 0 || lngNum === 0 ||
    latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180
  ) {
    return null;
  }
  return { lat: latNum, lng: lngNum };
}

export interface WithDistance<T> {
  item: T;
  coords: LatLng;
  distanceKm: number;
}

/**
 * Dado un centro y una lista de elementos con coordenadas, devuelve los que
 * tienen coordenadas válidas, ordenados por cercanía y (opcionalmente) acotados
 * a un radio máximo en km.
 */
export function nearestTo<T>(
  center: LatLng,
  items: T[],
  getCoords: (item: T) => { lat: string | number | null | undefined; lng: string | number | null | undefined },
  opts?: { radiusKm?: number; limit?: number },
): WithDistance<T>[] {
  const result: WithDistance<T>[] = [];

  for (const item of items) {
    const raw = getCoords(item);
    const coords = parseCoords(raw.lat, raw.lng);
    if (!coords) continue;
    const distanceKm = haversineKm(center, coords);
    if (opts?.radiusKm != null && distanceKm > opts.radiusKm) continue;
    result.push({ item, coords, distanceKm });
  }

  result.sort((a, b) => a.distanceKm - b.distanceKm);
  return opts?.limit != null ? result.slice(0, opts.limit) : result;
}

/**
 * Ordena una lista por cercanía a un centro CONSERVANDO todos los elementos.
 * A diferencia de nearestTo, no recorta por radio ni descarta sin-coords:
 * los que no tienen coordenadas válidas quedan al final con distanceKm = null.
 * Pensado para el buscador: reordena los resultados sin perder ninguno.
 */
export function sortByDistance<T>(
  center: LatLng,
  items: T[],
  getCoords: (item: T) => { lat: string | number | null | undefined; lng: string | number | null | undefined },
): { item: T; distanceKm: number | null }[] {
  return items
    .map((item) => {
      const raw = getCoords(item);
      const coords = parseCoords(raw.lat, raw.lng);
      return { item, distanceKm: coords ? haversineKm(center, coords) : null };
    })
    .sort((a, b) => {
      if (a.distanceKm === null) return b.distanceKm === null ? 0 : 1;
      if (b.distanceKm === null) return -1;
      return a.distanceKm - b.distanceKm;
    });
}

/** Formatea una distancia en km de forma legible. */
export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

// Tabla localidad -> centro (lat/lng), derivada de las coordenadas reales de
// los prestadores en producción. Permite ubicar una localidad sin geocoder.
const CENTROS = localidadCentros as Record<string, { lat: number; lng: number; n: number }>;

/**
 * Normaliza el nombre de una localidad para usarlo como clave de búsqueda:
 * mayúsculas, sin tildes, sin espacios extra. Debe coincidir con la
 * normalización usada al construir la tabla de centros.
 */
export function normalizeLocalityName(s: string | null | undefined): string {
  return (s || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ");
}

/** Centro (lat/lng) de una localidad por nombre, o null si no está en la tabla. */
export function localidadCenter(name: string | null | undefined): LatLng | null {
  const c = CENTROS[normalizeLocalityName(name)];
  return c ? { lat: c.lat, lng: c.lng } : null;
}

// Último fallback cuando la localidad elegida no tiene prestadores geolocalizados
// (no entra en CENTROS, que se deriva de esos prestadores) — típicamente un
// pueblo chico sin cobertura propia. Sin esto el mapa se queda sin centro y no
// se reposiciona, mostrando lo que haya quedado de una búsqueda anterior.
// Coordenadas de referencia (capital o ciudad principal) de cada provincia.
const PROVINCIA_CENTROS: Record<string, LatLng> = {
  "CAPITAL FEDERAL": { lat: -34.6037, lng: -58.3816 },
  "BUENOS AIRES": { lat: -34.9215, lng: -57.9545 },
  "CATAMARCA": { lat: -28.4696, lng: -65.7852 },
  "CHACO": { lat: -27.4512, lng: -58.9867 },
  "CHUBUT": { lat: -43.3002, lng: -65.1023 },
  "CORDOBA": { lat: -31.4201, lng: -64.1888 },
  "CORRIENTES": { lat: -27.4692, lng: -58.8306 },
  "ENTRE RIOS": { lat: -31.7333, lng: -60.5297 },
  "FORMOSA": { lat: -26.1849, lng: -58.1731 },
  "JUJUY": { lat: -24.1858, lng: -65.2995 },
  "LA PAMPA": { lat: -36.6167, lng: -64.2833 },
  "LA RIOJA": { lat: -29.4131, lng: -66.8558 },
  "MENDOZA": { lat: -32.8895, lng: -68.8458 },
  "MISIONES": { lat: -27.3621, lng: -55.9008 },
  "NEUQUEN": { lat: -38.9516, lng: -68.0591 },
  "RIO NEGRO": { lat: -40.8135, lng: -62.9967 },
  "SALTA": { lat: -24.7859, lng: -65.4117 },
  "SAN JUAN": { lat: -31.5375, lng: -68.5364 },
  "SAN LUIS": { lat: -33.3017, lng: -66.3378 },
  "SANTA CRUZ": { lat: -51.6230, lng: -69.2168 },
  "SANTA FE": { lat: -31.6333, lng: -60.7000 },
  "SANTIAGO DEL ESTERO": { lat: -27.7834, lng: -64.2642 },
  "TIERRA DEL FUEGO": { lat: -54.8019, lng: -68.3030 },
  "TUCUMAN": { lat: -26.8241, lng: -65.2226 },
};

/** Centro (lat/lng) de una provincia por nombre, o null si no se reconoce. */
export function provinciaCenter(name: string | null | undefined): LatLng | null {
  return PROVINCIA_CENTROS[normalizeLocalityName(name)] ?? null;
}

export interface NearestLocality {
  /** Nombre normalizado de la localidad (clave de la tabla de centros). */
  name: string;
  coords: LatLng;
  distanceKm: number;
  /** Cantidad de prestadores geolocalizados que originaron este centro. */
  count: number;
}

/**
 * Localidad de la tabla de centros más cercana a un punto. Se usa para derivar
 * la zona a consultar a partir del GPS del dispositivo, sin geocoder externo.
 */
export function nearestLocality(point: LatLng): NearestLocality | null {
  let best: NearestLocality | null = null;
  for (const name in CENTROS) {
    const c = CENTROS[name];
    const distanceKm = haversineKm(point, { lat: c.lat, lng: c.lng });
    if (!best || distanceKm < best.distanceKm) {
      best = { name, coords: { lat: c.lat, lng: c.lng }, distanceKm, count: c.n };
    }
  }
  return best;
}

/** Centroide (promedio) de una lista de coordenadas, o null si está vacía. */
export function centroidOf(points: LatLng[]): LatLng | null {
  if (!points.length) return null;
  const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const lng = points.reduce((s, p) => s + p.lng, 0) / points.length;
  return { lat, lng };
}

export interface HybridRanked<T> {
  item: T;
  /** Distancia REAL al centro (solo si el prestador tiene coords válidas), o null. */
  distanceKm: number | null;
  /** Distancia ESTIMADA vía centro de la localidad, usada solo para ordenar (no es exacta). */
  estimatedKm: number | null;
}

/**
 * Ordena por cercanía con estrategia híbrida pensada para no dejar afuera a los
 * prestadores sin geolocalización. Conserva TODOS los elementos en tres niveles:
 *
 *  1. Con coordenadas reales válidas → primero, ordenados por distancia real.
 *  2. Sin coordenadas pero con localidad conocida en la tabla de centros →
 *     después, ordenados por la distancia estimada al centro de su localidad.
 *  3. Sin coordenadas y sin localidad en la tabla → al final, en su orden original.
 *
 * Como `localidad-centros.json` se deriva de las coordenadas reales de los
 * prestadores, la localidad del prestador localizado más cercano siempre está en
 * la tabla: por eso un prestador sin coords de esa misma localidad cae en el
 * nivel 2 y sube dentro de su grupo, quedando cerca de los resultados relevantes.
 *
 * `distanceKm` es la distancia real (solo nivel 1) para mostrar como badge;
 * `estimatedKm` es la estimación (nivel 2) y NO debe presentarse como exacta.
 */
export function sortByDistanceHybrid<T>(
  center: LatLng,
  items: T[],
  getCoords: (item: T) => { lat: string | number | null | undefined; lng: string | number | null | undefined },
  getLocality: (item: T) => string | null | undefined,
): HybridRanked<T>[] {
  const ranked = items.map((item, index) => {
    const coords = parseCoords(getCoords(item).lat, getCoords(item).lng);
    if (coords) {
      return { item, index, tier: 0, distanceKm: haversineKm(center, coords), estimatedKm: null };
    }
    const lc = localidadCenter(getLocality(item));
    if (lc) {
      return { item, index, tier: 1, distanceKm: null, estimatedKm: haversineKm(center, lc) };
    }
    return { item, index, tier: 2, distanceKm: null, estimatedKm: null };
  });

  ranked.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.tier === 0) return (a.distanceKm as number) - (b.distanceKm as number);
    if (a.tier === 1) return (a.estimatedKm as number) - (b.estimatedKm as number);
    return a.index - b.index; // nivel 2: preservar orden original
  });

  return ranked.map(({ item, distanceKm, estimatedKm }) => ({ item, distanceKm, estimatedKm }));
}

/** Puntos de referencia para la demo (centros de ciudades). */
export const PRESET_CENTERS: { label: string; coords: LatLng }[] = [
  { label: 'CABA', coords: { lat: -34.6037, lng: -58.3816 } },
  { label: 'La Plata', coords: { lat: -34.9215, lng: -57.9545 } },
  { label: 'Rosario', coords: { lat: -32.9442, lng: -60.6505 } },
  { label: 'Córdoba', coords: { lat: -31.4201, lng: -64.1888 } },
  { label: 'Mendoza', coords: { lat: -32.8895, lng: -68.8458 } },
  { label: 'Mar del Plata', coords: { lat: -38.0055, lng: -57.5426 } },
];
